import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import HexagonRoundedIcon from '@mui/icons-material/HexagonRounded'
import { motion } from 'framer-motion'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { tokens } from '@theme/tokens'
import { ROUTES } from '@routes/paths'
import { APP_NAME } from '@utils/constants'
import { useAuth } from '@hooks/useAuth'

interface SidebarProps {
  open: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  to: string
  icon: React.ReactNode
  roles?: string[]
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', to: ROUTES.dashboard, icon: <HomeOutlinedIcon fontSize="small" /> },
  {
    label: 'Users',
    to: ROUTES.users,
    icon: <PeopleOutlinedIcon fontSize="small" />,
    roles: ['admin'],
  },
  { label: 'Bookings', to: ROUTES.bookings, icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { label: 'Feedback', to: ROUTES.feedback, icon: <RateReviewOutlinedIcon fontSize="small" /> },
  {
    label: 'Notifications',
    to: ROUTES.notifications,
    icon: <NotificationsOutlinedIcon fontSize="small" />,
    roles: ['admin'],
  },
  {
    label: 'Monetization',
    to: ROUTES.monetization,
    icon: <AttachMoneyOutlinedIcon fontSize="small" />,
    roles: ['admin'],
  },
  {
    label: 'AI Usage',
    to: ROUTES.aiUsage,
    icon: <SmartToyOutlinedIcon fontSize="small" />,
    roles: ['admin'],
  },
  {
    label: 'Lookups',
    to: ROUTES.lookups,
    icon: <ListAltOutlinedIcon fontSize="small" />,
    roles: ['admin'],
  },
  { label: 'Profile', to: ROUTES.profile, icon: <PersonOutlineOutlinedIcon fontSize="small" /> },
  { label: 'Settings', to: ROUTES.settings, icon: <SettingsOutlinedIcon fontSize="small" /> },
]

const Logo = () => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 1 }}>
    <Box
      sx={(theme) => ({
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        color: theme.palette.text.primary,
      })}
    >
      <HexagonRoundedIcon sx={{ fontSize: 28 }} />
    </Box>
    <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em">
      {APP_NAME.toLowerCase().replace(/\s+/g, '')}
    </Typography>
  </Stack>
)

const SidebarContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const role = user?.role ?? ''
  const visibleNav = NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role))

  const handleLogout = () => {
    logout()
    navigate(ROUTES.login, { replace: true })
  }

  return (
    <Stack sx={{ height: '100%', px: 1.5, py: 2.5 }} spacing={2}>
      <Logo />

      <Box sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
        <List sx={{ p: 0 }} disablePadding>
          {visibleNav.map((item, idx) => {
            const selected =
              item.to === ROUTES.dashboard
                ? location.pathname === ROUTES.dashboard || location.pathname === ROUTES.root
                : location.pathname.startsWith(item.to)
            return (
              <motion.div
                key={item.to}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  selected={selected}
                  sx={{
                    mb: 0.25,
                    py: 1,
                    px: 1.25,
                    transition: tokens.transition.short,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 600 : 500 }}
                  />
                </ListItemButton>
              </motion.div>
            )
          })}
        </List>
      </Box>

      <List sx={{ p: 0 }} disablePadding>
        <ListItemButton sx={{ py: 1, px: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <HelpOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Help & information"
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
        <ListItemButton onClick={handleLogout} sx={{ py: 1, px: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Log out"
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
      </List>
    </Stack>
  )
}

export const Sidebar = ({ open, onClose }: SidebarProps) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const paperSx = {
    width: tokens.drawerWidth,
    boxSizing: 'border-box' as const,
    borderTopRightRadius: tokens.borderRadiusLarge,
    borderBottomRightRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  }

  if (isDesktop) {
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          width: tokens.drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        <SidebarContent />
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': paperSx }}
    >
      <SidebarContent />
    </Drawer>
  )
}
